import { Trans, useLingui } from '@lingui/react/macro';
import { CalendarClock, Info } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { WorkerConflict } from '@/domain/rotagen/proposal-day-data';

interface WorkerConflictsProps {
  /** Workers with recurring working-time / availability conflicts. */
  conflicts: WorkerConflict[];
}

/**
 * A callout flagging workers with recurring scheduling conflicts (rest, days
 * off, max-consecutive, overlap…) across the week — the issues a manager should
 * resolve before applying, distinct from a shift simply being short. A single
 * conflict reads in full prose; several collapse to a count + named chips, each
 * with an info tooltip carrying that worker's detail. Read-only.
 */
export function WorkerConflicts({ conflicts }: WorkerConflictsProps) {
  const { t } = useLingui();

  if (conflicts.length === 0) return null;

  if (conflicts.length === 1) {
    const [conflict] = conflicts;
    const name = conflict.workerName ?? conflict.workerId;
    return (
      <Alert className="border-warning-500/40 bg-warning-500/5">
        <CalendarClock className="size-4" />
        <AlertTitle>
          <Trans>Recurring conflict — {name}</Trans>
        </AlertTitle>
        <AlertDescription>
          <ConflictDetail conflict={conflict} />.{' '}
          <Trans>It may be worth reviewing their schedule, or the template, before applying.</Trans>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div
      role="status"
      className="space-y-2 rounded-lg border border-warning-500/40 bg-warning-500/5 p-4 text-center"
    >
      <p className="flex items-center justify-center gap-2 font-medium">
        <CalendarClock className="size-4 shrink-0" />
        <Trans>{conflicts.length} workers have recurring conflicts</Trans>
      </p>
      <TooltipProvider delayDuration={0}>
        <ul className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          {conflicts.map((conflict) => {
            const name = conflict.workerName ?? conflict.workerId;
            return (
              <li key={conflict.workerId} className="inline-flex items-center gap-1">
                <span className="font-medium text-foreground">{name}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t`Conflict details for ${name}`}
                      className="inline-flex text-muted-foreground hover:text-foreground"
                    >
                      <Info className="size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <ConflictDetail conflict={conflict} />
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </TooltipProvider>
      <p className="text-xs text-muted-foreground">
        <Trans>Review their schedules or the template before applying.</Trans>
      </p>
    </div>
  );
}

/** The rules a worker hit and how many shifts — shared by the prose form and the tooltip. */
function ConflictDetail({ conflict }: { conflict: WorkerConflict }) {
  return (
    <>
      {conflict.rules.join(', ')}{' '}
      {conflict.shiftCount === 1 ? (
        <Trans>on 1 shift</Trans>
      ) : (
        <Trans>on {conflict.shiftCount} shifts</Trans>
      )}
    </>
  );
}
